import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";

const faqs = [
  {
    category: "General",
    questions: [
      { q: "What courses does Vedanti Academy offer?", a: "Vedanti Academy offers comprehensive coaching for Banking exams (IBPS PO/Clerk, SBI PO/Clerk, RBI Assistant, RRB), SSC exams (CGL, CHSL, MTS), Railway exams (NTPC, Group D), MBA entrance (MBA CET, SNAP, CMAT, MAT), Insurance exams (LIC, GIC, NIACL), and other government exams like NABARD, IB ACIO, FCI, India Post, EPFO/ESIC, and BBA CET." },
      { q: "Where are Vedanti Academy branches located?", a: "Our branch is located in Badlapur (3rd Floor, Jadhav Market, above Aradhana Hotel, Badlapur, Maharashtra 421503). We serve students from Badlapur, Ambernath, Ulhasnagar, Kalyan, Dombivli, Karjat, Neral, Vangni, Khopoli, and many more locations." },
      { q: "What makes Vedanti Academy different from other coaching classes?", a: "We offer 360-degree Hybrid Doubt Support (offline + online), Selection-Centric Mentorship with 'Upto Selection Support,' Smart Learning Environment with AC classrooms and digital boards, Student-First Approach with personalized attention, Updated Exam Patterns for 2025-26, and extra features like In-House Library and prime location near Badlapur Station." },
      { q: "Do you offer free demo classes?", a: "Yes! We offer free demo classes for all our courses. You can book a demo class through our website or by calling us at +91 8308776505." },
    ],
  },
  {
    category: "Banking Exams",
    questions: [
      { q: "What is the eligibility for IBPS PO exam?", a: "Graduate in any discipline from a recognized university. Age limit is 20-30 years. The exam has three stages: Prelims (100 marks, 60 min), Mains (200 marks, 180 min), and Interview." },
      { q: "What is the difference between IBPS Clerk and IBPS PO?", a: "IBPS Clerk recruits for clerical cadre positions while IBPS PO recruits for Probationary Officer positions. PO has a higher salary grade and includes an interview round. Both exams have Prelims and Mains stages." },
      { q: "How long does it take to prepare for banking exams?", a: "With dedicated preparation at Vedanti Academy, most students are exam-ready within 4-6 months. We offer both regular and weekend batches to accommodate different schedules." },
    ],
  },
  {
    category: "SSC Exams",
    questions: [
      { q: "What is SSC CGL?", a: "SSC CGL (Combined Graduate Level) is conducted by the Staff Selection Commission for recruitment to Group B and Group C posts in various central government departments. It has Tier 1 (200 marks, 60 min) and Tier 2 (450 marks, 150 min) stages." },
      { q: "What is the eligibility for SSC MTS?", a: "10th Pass (Matriculation) from a recognized board. Age limit is 18-25 years. SSC MTS recruits for Multi Tasking Staff positions in central government departments." },
      { q: "Can I prepare for SSC and Banking exams simultaneously?", a: "Yes! Many subjects overlap between SSC and Banking exams like Quantitative Aptitude, Reasoning, English, and General Awareness. At Vedanti Academy, we offer combined preparation strategies." },
    ],
  },
  {
    category: "MBA Entrance",
    questions: [
      { q: "What is MAH MBA CET?", a: "MAH MBA CET is the Maharashtra state-level entrance exam for MBA/MMS admissions. It has 200 marks with 150 minutes duration, covering Logical Reasoning, Abstract Reasoning, Quantitative Aptitude, and Verbal Ability." },
      { q: "When should I start preparing for MBA CET?", a: "Ideally, 4-6 months before the exam. Our coaching includes shortcuts and strategies that help students master the CET pattern quickly." },
    ],
  },
  {
    category: "Railway Exams",
    questions: [
      { q: "What is RRB NTPC exam?", a: "RRB NTPC (Non-Technical Popular Categories) is conducted for various non-technical positions in Indian Railways. CBT-1 has 100 marks (90 min) covering Mathematics, Reasoning, and General Awareness. CBT-2 has 120 marks (90 min)." },
      { q: "What is the eligibility for Railway Group D?", a: "10th Pass + ITI or equivalent qualification. Age limit is 18-33 years. The exam covers Mathematics, Reasoning, General Science, and General Awareness." },
    ],
  },
  {
    category: "Fees & Batches",
    questions: [
      { q: "What are the batch timings?", a: "We offer Morning, Afternoon, Evening, and Weekend batches to suit different schedules. Contact us for the latest batch timings." },
      { q: "Do you offer online classes?", a: "Yes, we offer hybrid learning with both offline and online classes. Students get digital access to teachers and resources for resolving doubts during self-study hours." },
      { q: "How can I enroll?", a: "You can enroll by visiting our Badlapur branch, booking a consultancy through our website, or calling us at +91 8308776505. We also offer free demo classes before enrollment." },
    ],
  },
];

export default function FAQPage() {
  return (
    <div data-testid="faq-page">
      <Helmet>
        <title>FAQs - Frequently Asked Questions | Vedanti Academy</title>
        <meta name="description" content="Find answers to common questions about Vedanti Academy's courses, fees, branches, demo classes, coaching methodology, and competitive exam preparation for Banking, SSC, MBA & Railway." />
        <meta name="keywords" content="Vedanti Academy FAQ, coaching questions, competitive exam coaching FAQ, banking coaching queries, SSC preparation questions" />
        <meta property="og:title" content="Frequently Asked Questions | Vedanti Academy" />
        <meta property="og:description" content="Get answers about our courses, fees, branches, methodology & more at Vedanti Academy." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://vedantiacademy.com/faq" />
        <link rel="canonical" href="https://vedantiacademy.com/faq" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqs.flatMap(cat => cat.questions.map(item => ({
            "@type": "Question",
            "name": item.q,
            "acceptedAnswer": {"@type": "Answer", "text": item.a}
          })))
        })}</script>
      </Helmet>
      <section className="bg-vedanti-navy py-12 md:py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(249,115,22,0.15)_0%,_transparent_60%)]"></div>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-vedanti-orange via-vedanti-mango to-vedanti-orange"></div>
        <div className="container-main relative z-10">
          <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-white">Frequently Asked <span className="text-vedanti-orange">Questions</span></h1>
          <p className="text-slate-300 mt-3 text-lg">Find answers to common questions about our courses and coaching</p>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-main max-w-4xl">
          {faqs.map((cat) => (
            <div key={cat.category} className="mb-10" data-testid={`faq-category-${cat.category.toLowerCase().replace(/\s/g, "-")}`}>
              <h2 className="font-heading text-2xl font-bold text-vedanti-navy mb-4 flex items-center gap-2">
                <div className="w-1.5 h-6 bg-vedanti-orange rounded-full"></div>
                {cat.category}
              </h2>
              <Accordion type="single" collapsible className="space-y-2">
                {cat.questions.map((faq, idx) => (
                  <AccordionItem key={faq.q} value={`${cat.category}-${idx}`} className="border border-slate-100 rounded-xl px-4 bg-slate-50/50">
                    <AccordionTrigger className="text-left font-medium text-vedanti-navy hover:text-vedanti-orange py-4 text-sm md:text-base" data-testid={`faq-q-${cat.category.toLowerCase().replace(/\s/g, "-")}-${idx}`}>
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-slate-600 text-sm leading-relaxed pb-4">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          ))}

          <div className="text-center mt-12 p-8 bg-slate-50 rounded-2xl border border-slate-100">
            <h3 className="font-heading text-xl font-bold text-vedanti-navy mb-2">Still have questions?</h3>
            <p className="text-slate-500 mb-4">Get in touch with us for personalized guidance</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/consultancy" className="bg-vedanti-orange text-white hover:bg-vedanti-orange/90 rounded-lg px-6 py-3 font-semibold transition-all" data-testid="faq-consultancy-btn">
                Book Free Consultancy
              </Link>
              <a href="tel:+918308776505" className="border-2 border-vedanti-navy text-vedanti-navy hover:bg-vedanti-navy hover:text-white rounded-lg px-6 py-3 font-medium transition-colors" data-testid="faq-call-btn">
                Call: +91 8308776505
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
